import React, { useMemo } from "react";
import { LuTrophy, LuFlipHorizontal } from "react-icons/lu";

const RANK_CONFIGS = [
  {
    rankColor: "#2563eb",
    bgSoft: "rgba(37, 99, 235, 0.08)",
    borderSoft: "rgba(37, 99, 235, 0.25)",
  },
  {
    rankColor: "#0284c7",
    bgSoft: "rgba(2, 132, 199, 0.08)",
    borderSoft: "rgba(2, 132, 199, 0.25)",
  },
  {
    rankColor: "#0d9488",
    bgSoft: "rgba(13, 148, 136, 0.08)",
    borderSoft: "rgba(13, 148, 136, 0.25)",
  },
  {
    rankColor: "#6366f1",
    bgSoft: "rgba(99, 102, 241, 0.08)",
    borderSoft: "rgba(99, 102, 241, 0.25)",
  },
];

const formatCurrency = (val) => {
  return `₹${Number(val || 0).toLocaleString("en-IN")}`;
};

export default function TopOrgsFlipCards({ organizations = [], totalDealValue = 0, loading = false }) {
  const topOrgs = useMemo(() => {
    return [...organizations]
      .sort((a, b) => (b.deal_price ?? b.price ?? 0) - (a.deal_price ?? a.price ?? 0))
      .slice(0, 4);
  }, [organizations]);

  const top4Sum = useMemo(() => {
    return topOrgs.reduce((acc, curr) => acc + (curr.deal_price ?? curr.price ?? 0), 0);
  }, [topOrgs]);

  const maxOrgVal = useMemo(() => {
    return (topOrgs[0]?.deal_price ?? topOrgs[0]?.price ?? 0) || 1;
  }, [topOrgs]);

  const top4Pct = totalDealValue > 0 ? ((top4Sum / totalDealValue) * 100).toFixed(0) : 0;

  if (loading && topOrgs.length === 0) {
    return (
      <section className="top-orgs-section">
        <div className="top-orgs-header">
          <div className="top-orgs-title">
            <LuTrophy style={{ color: "var(--primary)" }} />
            <span>Top Organizations</span>
          </div>
        </div>
        <div className="top-orgs-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={`flip-skel-${i}`} className="flip-card">
              <div className="flip-card-front skeleton-box" style={{ height: "160px" }} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (topOrgs.length === 0) {
    return null;
  }

  return (
    <section className="top-orgs-section" data-testid="top-orgs-section">
      <div className="top-orgs-header">
        <div className="top-orgs-title">
          <LuTrophy style={{ color: "var(--primary)" }} />
          <span>Top Organizations</span>
          {top4Pct > 0 && (
            <span className="pill" style={{ fontSize: "11px" }}>
              Top {topOrgs.length} hold {top4Pct}% of deal value
            </span>
          )}
        </div>
        <span className="flip-hint-badge">
          <LuFlipHorizontal />
          Hover card to flip & view details
        </span>
      </div>

      <div className="top-orgs-grid" id="topOrgsGrid">
        {topOrgs.map((org, idx) => {
          const cfg = RANK_CONFIGS[idx % RANK_CONFIGS.length];
          const val = org.deal_price ?? org.price ?? 0;
          const leads = org.leads ?? 0;
          const deals = org.deals ?? 0;
          const owner = org.owner || "Account Lead";
          const pctOfCrm =
            totalDealValue > 0
              ? ((val / totalDealValue) * 100).toFixed(1)
              : top4Sum > 0
              ? ((val / top4Sum) * 100).toFixed(1)
              : "0.0";
          const progressWidth =
            maxOrgVal > 0 ? Math.min(100, Math.max(6, Math.round((val / maxOrgVal) * 100))) : 0;

          return (
            <div className="flip-card" key={idx} tabIndex={0} data-testid={`top-org-card-${idx}`}>
              <div className="flip-card-inner">
                {/* Front of Card */}
                <div
                  className="flip-card-front"
                  style={{ borderTop: `3px solid ${cfg.rankColor}` }}
                >
                  <div className="flip-card-top">
                    <span
                      className="org-rank-badge"
                      style={{
                        background: cfg.bgSoft,
                        color: cfg.rankColor,
                        borderColor: cfg.borderSoft,
                      }}
                    >
                      #{idx + 1} Top Org
                    </span>
                    <span className="org-pct-badge">{pctOfCrm}% of CRM</span>
                  </div>
                  <div className="flip-card-mid">
                    <div className="org-name-title" title={org.organization}>
                      {org.organization}
                    </div>
                    <div className="org-val-title">{formatCurrency(val)}</div>
                  </div>
                  <div className="flip-card-bot">
                    <span className="flip-touch-hint">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="23 4 23 10 17 10" />
                        <polyline points="1 20 1 14 7 14" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                      </svg>
                      Hover to flip details
                    </span>
                    <div className="org-mini-progress">
                      <div
                        className="org-mini-fill"
                        style={{ width: `${progressWidth}%`, background: cfg.rankColor }}
                      />
                    </div>
                  </div>
                </div>

                {/* Back of Card (No Tier Tag) */}
                <div
                  className="flip-card-back"
                  style={{ borderTop: `3px solid ${cfg.rankColor}` }}
                >
                  <div className="flip-back-header">
                    <div className="flip-back-name" title={org.organization}>
                      {org.organization}
                    </div>
                  </div>
                  <div className="flip-metrics-grid">
                    <div className="flip-metric-item">
                      <span className="flip-metric-label">Deal Value</span>
                      <span
                        className="flip-metric-val"
                        style={{ color: cfg.rankColor, fontSize: "11.5px" }}
                      >
                        {formatCurrency(val)}
                      </span>
                    </div>
                    <div className="flip-metric-item">
                      <span className="flip-metric-label">No of Leads</span>
                      <span className="flip-metric-val">{leads} Leads</span>
                    </div>
                    <div className="flip-metric-item">
                      <span className="flip-metric-label">No of Deals</span>
                      <span className="flip-metric-val">{deals} Deals</span>
                    </div>
                    <div className="flip-metric-item">
                      <span className="flip-metric-label">Account Owner</span>
                      <span className="flip-metric-val" title={owner}>
                        {owner}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
